<template>
  <v-dialog
    v-model="showDialog"
    width="90%"
    :persistent="importing"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <div
      class="ant-glass-dialog px-10 py-5 d-flex flex-column pos-rel full-width"
      style="height: 600px"
    >
      <v-overlay :value="importing" absolute opacity="0.85">
        <div class="d-flex flex-column align-center justify-center px-6">
          <v-progress-circular
            color="primary"
            indeterminate
            size="56"
            width="4"
          />
          <div class="mt-4 text-center">
            <div class="font-weight-medium" style="font-size: 14px">
              Importing tasks…
            </div>
            <div class="mt-1" style="font-size: 12px; opacity: 0.9">
              Step: {{ importProgress.stepLabel }}
            </div>
            <div class="mt-1" style="font-size: 12px; opacity: 0.9">
              Batch: {{ importProgress.batchCurrent }}/{{
                importProgress.batchTotal
              }}
              <span v-if="importProgress.batchSize">
                ({{ importProgress.batchSize }} tasks per batch)
              </span>
            </div>
            <v-progress-linear
              class="mt-3"
              color="primary"
              height="6"
              :value="importProgress.percent"
            />
            <div class="mt-2" style="font-size: 12px; opacity: 0.9">
              {{ importProgress.processed }}/{{ importProgress.total }} tasks
              processed
            </div>
            <div class="mt-2" style="font-size: 11px; opacity: 0.8">
              Please don’t close this dialog or page until the import finishes.
            </div>
          </div>
        </div>
      </v-overlay>

      <div class="d-flex flex-scroll-height">
        <div class="flex-1 d-flex flex-column">
          <ant-input label="Module Types" top-margin="mt-0">
            <template #input-field>
              <v-select
                ref="moduleTypeSelect"
                v-model="pendingModuleTypes"
                :items="moduleTypeItems"
                dense
                filled
                hide-details
                multiple
                placeholder="Module types"
                style="flex: 0"
                :disabled="importing"
                :loading="loadingElements"
                @blur="applyModuleTypes"
                @change="scheduleApplyModuleTypes"
              >
                <template #prepend-item>
                  <div class="d-flex align-center px-4 pb-2">
                    <span class="fs-12 mr-2">Sort</span>
                    <v-btn-toggle
                      v-model="moduleTypeSort"
                      color="primary"
                      dense
                      group
                      mandatory
                    >
                      <v-btn value="name" x-small :disabled="importing">
                        A-Z
                      </v-btn>
                      <v-btn value="sequence" x-small :disabled="importing">
                        Assembly sequence
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                  <v-divider class="mb-2" />
                  <v-list-item
                    :disabled="importing"
                    ripple
                    @click="toggleAllModuleTypes"
                  >
                    <v-list-item-action>
                      <v-icon
                        :color="pendingModuleTypes.length ? 'primary' : ''"
                      >
                        {{ selectAllIcon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select all</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2" />
                </template>
                <!-- Returned without a v-list-item, so the select wraps it in
                     its own tile (selection, keyboard and aria included) -->
                <template #item="{ item, attrs }">
                  <v-list-item-action>
                    <v-simple-checkbox
                      color="primary"
                      :ripple="false"
                      :value="attrs.inputValue"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action
                    v-if="completedModuleTypes.has(item.value)"
                    class="d-flex flex-row align-center"
                  >
                    <v-icon color="success" small>mdi-check-circle</v-icon>
                    <span class="fs-10 font-italic ml-1">Done</span>
                  </v-list-item-action>
                </template>
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 3" class="mr-1" small>{{
                    item.value
                  }}</v-chip>
                  <span v-if="index === 3" class="fs-10 font-italic">
                    +{{ pendingModuleTypes.length - 3 }} more
                  </span>
                </template>
              </v-select>
            </template>
          </ant-input>
          <div
            v-if="hasSelection"
            class="d-flex flex-column flex-1 justify-start mt-5 overflow-y-auto"
          >
            <v-subheader
              class="font-weight-bold pa-0"
              style="font-size: 11px; height: auto"
            >
              Modules ({{ modulesBySelectedTypes.length }})
            </v-subheader>
            <div
              v-for="group in modulesGroupedByType"
              :key="group.type"
              class="d-flex flex-column"
            >
              <div class="d-flex align-center mt-2">
                <span class="fs-12 font-weight-medium">{{ group.type }}</span>
                <span
                  v-if="moduleTypeSort === 'sequence'"
                  class="fs-10 font-italic ml-2"
                  >from #{{ formatSequence(firstSequence(group.type)) }}</span
                >
                <span class="fs-10 font-italic ml-2"
                  >{{ group.modules.length }} modules</span
                >
                <v-spacer />
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :color="previewType === group.type ? 'primary' : ''"
                      :disabled="importing"
                      icon
                      x-small
                      v-bind="attrs"
                      @click="selectPreviewType(group.type)"
                      v-on="on"
                    >
                      <v-icon small>mdi-file-document-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Show 2D drawing</span>
                </v-tooltip>
              </div>
              <div>
                <v-chip
                  v-for="module in group.modules"
                  :key="module.id"
                  class="mr-2 my-1"
                  small
                  ><span
                    v-if="moduleTypeSort === 'sequence'"
                    class="fs-10 font-italic mr-1"
                    >#{{ formatSequence(moduleSequence(module)) }}</span
                  >{{ module.module_id }}
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSelection" class="px-5 flex-1 overflow-y-auto">
          <div
            v-for="locationItem in locationActivities"
            :key="locationItem.location.id"
            class="px-2 py-4 ant-border-bottom"
          >
            <v-subheader
              v-if="assemblyPreparationsHall.automated"
              class="pa-0 mb-2"
              style="height: auto"
            >
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    :disabled="locationItem.completed || importing"
                    :loading="locationItem.loading"
                    class="mr-2"
                    icon
                    v-bind="attrs"
                    @click="completeLocation(locationItem)"
                    v-on="on"
                  >
                    <v-icon :color="locationItem.completed ? 'success' : ''">
                      {{
                        locationItem.completed
                          ? 'mdi-check-circle'
                          : 'mdi-circle-outline'
                      }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>Complete location</span>
              </v-tooltip>
              Location {{ locationItem.location.assembly_location }} -
              {{ locationItem.location.description }}
              <span
                v-if="locationItem.pending < locationItem.total"
                class="fs-10 font-italic ml-2"
                >{{ locationItem.pending }} of {{ locationItem.total }} modules
                pending</span
              >
              <v-spacer />
              <v-checkbox
                v-if="!locationItem.completed"
                v-model="locationItem.toggle"
                :ripple="false"
                class="mt-0 pt-0"
                hide-details
                label="Select all"
                :disabled="importing"
                @click="selectAll(locationItem)"
              />
            </v-subheader>

            <v-subheader v-else class="pa-0 mb-2" style="height: auto">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon
                    :color="
                      locationItem.enabled || locationItem.completed
                        ? 'success'
                        : ''
                    "
                    :disabled="locationItem.completed || importing"
                    class="mr-2"
                    v-bind="attrs"
                    @click="completeLocation(locationItem)"
                    v-on="on"
                  >
                    {{
                      locationItem.enabled || locationItem.completed
                        ? 'mdi-check-circle'
                        : 'mdi-circle-outline'
                    }}
                  </v-icon>
                </template>
                <span>Complete location</span>
              </v-tooltip>
              Location
              <span
                v-if="locationItem.pending < locationItem.total"
                class="fs-10 font-italic ml-2"
                >{{ locationItem.pending }} of {{ locationItem.total }} modules
                pending</span
              >
              <v-spacer />
              <v-checkbox
                v-if="!locationItem.completed"
                v-model="locationItem.toggle"
                :ripple="false"
                class="mt-0 pt-0"
                hide-details
                label="Select all"
                :disabled="importing"
                @click="selectAll(locationItem)"
              />
            </v-subheader>

            <div v-if="!locationItem.completed" class="d-flex flex-column">
              <div
                v-for="step in assemblyLocationSteps(locationItem)"
                :key="`${locationItem.location.id}-step-${step.id}`"
              >
                <v-chip class="my-2" small
                  >{{ step.order }} - {{ step.title }}
                </v-chip>
                <div class="flex-1">
                  <div
                    v-for="activityItem in locationItem.activities
                      .filter((a) => a.step === step.id)
                      .sort((a, b) => a.order - b.order)"
                    :key="activityKey(activityItem)"
                    class="d-flex align-center fs-12"
                    :class="{
                      'preview-type-activity':
                        isPreviewTypeActivity(activityItem),
                    }"
                  >
                    <v-menu
                      v-if="activityItem.deviation"
                      v-model="deviationOrderMenus[activityItem.id]"
                      :close-on-content-click="false"
                      :nudge-width="200"
                      offset-x
                    >
                      <template #activator="{ on, attrs }">
                        <span
                          class="fs-10 font-italic d-flex text-primary c-pointer"
                          style="width: 30px"
                          v-on="on"
                        >
                          {{ activityItem.order }}
                        </span>
                      </template>

                      <v-card class="pa-2">
                        <ant-input label="Order" top-margin="mt-0">
                          <template #input-field>
                            <v-text-field
                              v-model="activityItem.order"
                              autofocus
                              dense
                              filled
                              hide-details
                              placeholder="0-100"
                              single-line
                              type="number"
                              :disabled="importing"
                              @keydown.enter="
                                updateOrderToDeviationTask(activityItem)
                              "
                            />
                          </template>
                        </ant-input>
                      </v-card>
                    </v-menu>
                    <span
                      v-else
                      class="fs-10 font-italic d-flex"
                      style="width: 30px"
                    >
                      {{ activityItem.order }}
                    </span>

                    <v-checkbox
                      v-model="activityItem.selected"
                      :ripple="false"
                      class="mt-0 pt-0"
                      dense
                      hide-details
                      :disabled="importing"
                    ></v-checkbox>
                    <v-icon
                      v-if="activityItem.deviation"
                      class="mr-2"
                      color="warning"
                      small
                      >mdi-alert-rhombus-outline
                    </v-icon>
                    {{ activityItem.activity }}
                    <span
                      v-if="activityItem.position"
                      class="font-italic ml-2 fs-10"
                      >{{ activityItem.position }}</span
                    >
                    <v-chip
                      v-if="
                        moduleTypes.length > 1 &&
                        activityItem.moduleType &&
                        activityItem.moduleType !== previewType
                      "
                      class="ml-2"
                      x-small
                      >{{ activityItem.moduleType }}</v-chip
                    >
                    <v-spacer />
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{
                          activityItem?.leanforms_q_nr
                        }}</span>
                      </template>
                      <span>{{
                        activityItem.leanforms_remaining_point ?? '-'
                      }}</span>
                    </v-tooltip>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-checkbox class="mt-0 pt-0" dense disabled hide-details />
                  <v-menu
                    v-model="
                      locationDeviationMenus[
                        `${locationItem.location.id}-${step.id}`
                      ]
                    "
                    :close-on-content-click="false"
                    :nudge-width="200"
                    offset-x
                  >
                    <template #activator="{ on, attrs }">
                      <v-icon
                        :ripple="false"
                        dense
                        v-bind="attrs"
                        :disabled="importing"
                        v-on="on"
                        >mdi-plus
                      </v-icon>
                    </template>

                    <v-card class="pa-2">
                      <ant-input label="Option" top-margin="mt-0">
                        <template #input-field>
                          <v-text-field
                            v-model="
                              locationDeviations[locationItem.location.id]
                            "
                            autofocus
                            dense
                            filled
                            hide-details
                            placeholder="title"
                            single-line
                            :disabled="importing"
                            @keydown.enter="
                              addLocationDeviation(locationItem, step)
                            "
                          />
                        </template>
                      </ant-input>
                    </v-card>
                  </v-menu>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Always rendered with a selection so the columns keep their width -->
        <div
          v-if="hasSelection"
          class="flex-1 d-flex align-center justify-center"
        >
          <iframe
            v-if="documentPreview"
            :src="documentPreview"
            height="100%"
            type="application/pdf"
            width="100%"
          />
          <v-progress-circular
            v-else-if="loadingPreview || loadingElements"
            color="primary"
            indeterminate
          />
          <span v-else class="fs-12 font-italic">No 2D drawing available</span>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { executeCustomModuleCall } from '@/services/api/module.api';
import { DHME_ASSEMBLY_PREPARATIONS } from '@/modules/modules';
import DHMEASMixin from '@/modules/daiwa-house-modular-europe/AssemblyPreparations/DhmeAssemblyPreparations.mixin';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'DhmeAssemblyPreparationsBatchDialog',
  components: { AntInput },
  mixins: [DHMEASMixin],
  props: {
    displayed: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      // What the select shows; applied to moduleTypes (which loads the
      // elements) once the user is done selecting
      pendingModuleTypes: [],
      applyTimer: null,
      moduleTypes: [],
      // 'name' (A-Z) or 'sequence' (first assembly sequence of the type)
      moduleTypeSort: 'name',
      previewType: null,
      loadingPreview: false,
      loadingElements: false,
      selectionToken: 0,
      previewToken: 0,
      preservedSelection: null,
      locationActivities: [],
      deviationOrderMenus: {},
      locationDeviationMenus: {},
      locationDeviations: {},
      elements: [],
      importing: false,
      documentPreview: null,

      importProgress: {
        stepLabel: '-',
        batchCurrent: 0,
        batchTotal: 0,
        batchSize: 50,
        processed: 0,
        total: 0,
        percent: 0,
      },

      beforeUnloadHandler: null,
    };
  },
  computed: {
    ...mapGetters([
      'project',
      'assemblyPreparationsModules',
      'assemblyPreparationsHall',
      'assemblyConfigurationSteps',
      'assemblyPreparationsHallLocations',
      'assemblyPreparationLocationActivities',
      'assemblyPreparationsLocationStatuses',
      'assemblyPreparationLocationTypes',
      'assemblyPreparationsModuleAssemblyTasks',
    ]),
    showDialog() {
      return this.displayed;
    },

    moduleId() {
      return this.project.modules.find(
        (module) => module.route === DHME_ASSEMBLY_PREPARATIONS
      ).id;
    },

    hasSelection() {
      return this.moduleTypes.length > 0;
    },

    // False while the select shows types that are not loaded yet
    moduleTypesApplied() {
      return (
        this.pendingModuleTypes.length === this.moduleTypes.length &&
        this.pendingModuleTypes.every((type) => this.moduleTypes.includes(type))
      );
    },

    modulesBySelectedTypes() {
      if (!this.moduleTypes.length) {
        return [];
      }
      const selected = new Set(this.moduleTypes);
      return this.assemblyPreparationsModules.filter((item) =>
        selected.has(item.module_type)
      );
    },

    modulesGroupedByType() {
      return [...this.moduleTypes].sort(this.compareModuleTypes).map((type) => {
        const modules = this.modulesBySelectedTypes.filter(
          (m) => m.module_type === type
        );
        if (this.moduleTypeSort === 'sequence') {
          modules.sort(
            (a, b) => this.moduleSequence(a) - this.moduleSequence(b)
          );
        }
        return { type, modules };
      });
    },

    uniqueModuleTypes() {
      return [
        ...new Set(
          this.assemblyPreparationsModules
            .map((item) => item.module_type)
            .filter(Boolean)
        ),
      ].sort(this.compareModuleTypes);
    },

    // The select lists types in the chosen order; by sequence it also shows
    // where each type first appears
    moduleTypeItems() {
      return this.uniqueModuleTypes.map((type) => ({
        value: type,
        text:
          this.moduleTypeSort === 'sequence'
            ? `${type} (from #${this.formatSequence(this.firstSequence(type))})`
            : type,
      }));
    },

    // Types of which every module completed every location of the hall; a
    // reopened location does not count as completed
    completedModuleTypes() {
      const locations = this.assemblyPreparationsHallLocations;
      const completed = new Set(
        this.assemblyPreparationsLocationStatuses
          .filter((s) => s.status !== 'reopened')
          .map((s) => `${s.module_id}::${s.assembly_location}`)
      );
      const modulesByType = new Map();
      this.assemblyPreparationsModules.forEach((m) => {
        if (!modulesByType.has(m.module_type)) {
          modulesByType.set(m.module_type, []);
        }
        modulesByType.get(m.module_type).push(m);
      });

      const done = new Set();
      if (!locations.length) {
        return done;
      }
      modulesByType.forEach((modules, type) => {
        if (
          modules.every((m) =>
            locations.every((l) => completed.has(`${m.module_id}::${l.id}`))
          )
        ) {
          done.add(type);
        }
      });
      return done;
    },

    // Lowest assembly sequence per module type, i.e. when it is first built
    firstSequenceByType() {
      const map = new Map();
      this.assemblyPreparationsModules.forEach((m) => {
        const sequence = this.moduleSequence(m);
        if (sequence < (map.get(m.module_type) ?? Infinity)) {
          map.set(m.module_type, sequence);
        }
      });
      return map;
    },

    selectAllIcon() {
      if (
        this.uniqueModuleTypes.length > 0 &&
        this.pendingModuleTypes.length === this.uniqueModuleTypes.length
      ) {
        return 'mdi-close-box';
      }
      if (this.pendingModuleTypes.length > 0) {
        return 'mdi-minus-box';
      }
      return 'mdi-checkbox-blank-outline';
    },
  },
  watch: {
    async moduleTypes(value) {
      // Guards against an older, slower selection overwriting a newer one
      const token = ++this.selectionToken;

      // While a previous change is still loading the list is already empty,
      // so keep the snapshot taken from the last list that was shown.
      if (this.locationActivities.length) {
        this.preservedSelection = this.snapshotSelection();
      }

      this.locationActivities = [];
      this.documentPreview = null;
      // Drops any drawing still loading for the previous selection
      this.previewToken++;
      this.loadingPreview = false;

      if (!value.length) {
        this.elements = [];
        this.previewType = null;
        this.preservedSelection = null;
        // A superseded in-flight request will not reset this itself
        this.loadingElements = false;
        return;
      }

      if (!value.includes(this.previewType)) {
        this.previewType = [...value].sort(this.compareModuleTypes)[0];
      }

      this.loadingElements = true;

      try {
        // Only types that were not loaded before in this dialog session
        const missingTypes = value.filter((type) => !this.elementsCache[type]);
        const fetched = await Promise.all(
          missingTypes.map((type) =>
            executeCustomModuleCall(
              this.project.id,
              this.moduleId,
              'fetchModuleTypeElements',
              {
                type: type,
              }
            )
          )
        );
        missingTypes.forEach((type, index) => {
          this.elementsCache[type] = fetched[index];
        });

        if (token !== this.selectionToken) {
          return;
        }

        this.elements = [].concat(
          ...value.map((type) => this.elementsCache[type])
        );
        this.buildLocationActivities();
        await this.loadPreviewDrawing();
      } catch (e) {
        if (token === this.selectionToken) {
          this.$store.commit('showNotification', {
            content: e?.message ?? 'Failed to load elements for module types',
            color: 'error',
          });
        }
      } finally {
        if (token === this.selectionToken) {
          this.loadingElements = false;
        }
      }
    },
  },

  created() {
    // Elements per module type, not reactive on purpose (large lists)
    this.elementsCache = {};
  },

  mounted() {
    this.beforeUnloadHandler = (e) => {
      if (!this.importing) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  },

  beforeDestroy() {
    clearTimeout(this.applyTimer);
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
  },

  methods: {
    // Modules without a usable sequence sort last
    moduleSequence(module) {
      const sequence = parseFloat(module.assembly_sequence);
      return Number.isFinite(sequence) ? sequence : Infinity;
    },

    firstSequence(type) {
      return this.firstSequenceByType.get(type) ?? Infinity;
    },

    formatSequence(sequence) {
      return Number.isFinite(sequence) ? sequence : '-';
    },

    compareModuleTypes(a, b) {
      if (this.moduleTypeSort === 'sequence') {
        const diff = this.firstSequence(a) - this.firstSequence(b);
        // Infinity - Infinity is NaN, so types without a sequence fall
        // through to the name order
        if (diff) {
          return diff;
        }
      }
      return a.localeCompare(b);
    },

    // Splits an array into fixed-size chunks
    chunkArray(list, size) {
      const chunks = [];
      for (let i = 0; i < list.length; i += size) {
        chunks.push(list.slice(i, i + size));
      }
      return chunks;
    },

    setProgress({ stepLabel, batchCurrent, batchTotal, processed, total }) {
      if (stepLabel !== undefined) this.importProgress.stepLabel = stepLabel;
      if (batchCurrent !== undefined)
        this.importProgress.batchCurrent = batchCurrent;
      if (batchTotal !== undefined) this.importProgress.batchTotal = batchTotal;
      if (processed !== undefined) this.importProgress.processed = processed;
      if (total !== undefined) this.importProgress.total = total;

      const t = this.importProgress.total || 0;
      const p = this.importProgress.processed || 0;
      this.importProgress.percent = t > 0 ? Math.round((p / t) * 100) : 0;
    },

    resetProgress() {
      this.importProgress.stepLabel = '-';
      this.importProgress.batchCurrent = 0;
      this.importProgress.batchTotal = 0;
      this.importProgress.batchSize = 50;
      this.importProgress.processed = 0;
      this.importProgress.total = 0;
      this.importProgress.percent = 0;
    },

    toggleAllModuleTypes() {
      if (this.pendingModuleTypes.length === this.uniqueModuleTypes.length) {
        this.pendingModuleTypes = [];
      } else {
        this.pendingModuleTypes = [...this.uniqueModuleTypes];
      }
      this.scheduleApplyModuleTypes();
    },

    // v-select has no menu-closed event, so apply shortly after the last
    // change once the menu is closed; blur applies straight away.
    scheduleApplyModuleTypes() {
      clearTimeout(this.applyTimer);
      this.applyTimer = setTimeout(() => {
        if (this.$refs.moduleTypeSelect?.isMenuActive) {
          this.scheduleApplyModuleTypes();
          return;
        }
        this.applyModuleTypes();
      }, 600);
    },

    applyModuleTypes() {
      clearTimeout(this.applyTimer);
      this.applyTimer = null;

      if (!this.moduleTypesApplied) {
        this.moduleTypes = [...this.pendingModuleTypes];
      }
    },

    selectPreviewType(type) {
      this.previewType = type;
      this.loadPreviewDrawing();
    },

    // The 2D drawing is module type specific, so it follows the type the user
    // asked to preview instead of the (arbitrary) first selected one.
    async loadPreviewDrawing() {
      // Guards against a slower, older drawing replacing the current one
      const token = ++this.previewToken;
      this.documentPreview = null;
      this.loadingPreview = false;

      if (!this.previewType) {
        return;
      }

      const match = this.modulesBySelectedTypes
        .filter((m) => m.module_type === this.previewType)
        .map((module) => ({
          module: module,
          task: this.assemblyPreparationsModuleAssemblyTasks.find(
            (t) =>
              t.sbscode?.code === module.build_nr &&
              t.appendixes?.some((a) => a.name === `2D_${module.module_id}`)
          ),
        }))
        .find((item) => item.task);

      if (!match) {
        return;
      }

      this.loadingPreview = true;
      try {
        await this.fetch2DDrawing(
          match.task,
          this.project.id,
          match.module.module_id,
          () => token === this.previewToken
        );
      } catch (e) {
        if (token === this.previewToken) {
          this.$store.commit('showNotification', {
            content: e?.message ?? 'Failed to load 2D drawing',
            color: 'error',
          });
        }
      } finally {
        if (token === this.previewToken) {
          this.loadingPreview = false;
        }
      }
    },

    buildLocationActivities() {
      this.locationActivities = this.assemblyPreparationsHallLocations.map(
        (location) => {
          const pending = this.modulesBySelectedTypes.filter(
            (m) =>
              !this.assemblyPreparationsLocationStatuses.find(
                (s) =>
                  s.module_id === m.module_id &&
                  location.id === s.assembly_location
              )
          );

          const object = {
            location: location,
            completed: pending.length === 0,
            pending: pending.length,
            total: this.modulesBySelectedTypes.length,
            loading: false,
            toggle: false,
            activities: [],
          };

          // Location activities are shared by every selected module type.
          // Element activities belong to the module type they came from, so
          // an element both types have is listed (and imported) once per type.
          // Element activities are told apart by origin, not by their element
          // type, so one without an element type can never pass for a
          // location activity and reach every module.
          const locationActivitySet = new Set(
            this.assemblyPreparationLocationActivities
          );
          const seen = new Set();
          this.moduleTypes.forEach((moduleType) => {
            this.getActivitiesByLocation(
              location,
              this.elementsCache[moduleType] ?? [],
              this.assemblyPreparationLocationTypes,
              this.assemblyPreparationLocationActivities
            ).forEach((activity) => {
              if (!locationActivitySet.has(activity)) {
                activity.moduleType = moduleType;
              }
              const key = this.activityKey(activity);
              if (seen.has(key)) {
                return;
              }
              seen.add(key);
              activity.deviation = false;
              activity.selected = false;
              object.activities.push(activity);
            });
          });

          this.restoreSelection(object);

          return object;
        }
      );
      this.preservedSelection = null;
    },

    // Element activities have no id; they are unique on module type + element
    // type + position
    activityKey(activity) {
      return activity.moduleType
        ? `element:${activity.moduleType}|${activity.type}|${
            activity.position ?? ''
          }`
        : `activity:${activity.id}`;
    },

    // Only worth marking when the list mixes more than one module type
    isPreviewTypeActivity(activity) {
      return (
        this.moduleTypes.length > 1 &&
        !!activity.moduleType &&
        activity.moduleType === this.previewType
      );
    },

    // Captures ticks and deviations per location so a module type change does
    // not throw away what the user already selected.
    snapshotSelection() {
      const snapshot = {};
      this.locationActivities.forEach((item) => {
        snapshot[item.location.id] = {
          toggle: item.toggle,
          selected: new Set(
            item.activities
              .filter((a) => a.selected && !a.deviation)
              .map((a) => this.activityKey(a))
          ),
          deviations: item.activities.filter((a) => a.deviation),
        };
      });
      return snapshot;
    },

    // Activities that no longer exist for the selected module types simply
    // drop out; new ones start unticked.
    restoreSelection(object) {
      const previous = this.preservedSelection?.[object.location.id];
      if (!previous) {
        return;
      }

      object.activities.forEach((a) => {
        a.selected = previous.selected.has(this.activityKey(a));
      });
      object.activities.push(...previous.deviations);
      object.toggle =
        previous.toggle &&
        object.activities.length > 0 &&
        object.activities.every((a) => a.selected);
    },

    selectAll(location) {
      location.activities.map((item) => {
        item.selected = location.toggle;
        return item;
      });
    },

    async completeLocation(locationItem) {
      if (this.importing) return;

      // The select applies on blur (async) or shortly after its menu closes,
      // so a click straight from the open menu lands before the new types are
      // loaded. Completing then would skip the newly picked types, deviations
      // included, and the rebuild mid-import would detach this location.
      if (this.loadingElements || !this.moduleTypesApplied) {
        this.applyModuleTypes();
        this.$store.commit('showNotification', {
          content:
            'Module type selection changed. Check the activities and complete the location again.',
          color: 'info',
        });
        return;
      }

      locationItem.loading = true;
      this.importing = true;
      this.resetProgress();

      let modules = this.modulesBySelectedTypes;
      let tasks = [];
      let locationStatuses = [];
      let skippedModules = [];
      let modulesWithoutAssemblyTask = [];

      let selectedActivities = locationItem.activities.filter(
        (a) => a.selected
      );

      try {
        for (let i = 0; i < modules.length; i++) {
          let build_nr = modules[i].build_nr;
          let module_id = modules[i].module_id;

          if (
            this.assemblyPreparationsLocationStatuses.find(
              (s) =>
                s.module_id === module_id &&
                locationItem.location.id === s.assembly_location
            )
          ) {
            skippedModules.push(module_id);
            continue;
          }

          const task = this.assemblyPreparationsModuleAssemblyTasks.find(
            (t) => t.sbscode?.code === build_nr
          );

          if (!task) {
            modulesWithoutAssemblyTask.push(module_id);
            continue;
          }

          const moduleElements = this.elements.filter(
            (e) => e.module_id === module_id
          );

          let tmpTasks = [];

          selectedActivities.forEach((a) => {
            // Element activities carry the module type they were listed for;
            // location activities and deviations never do.
            const element = a.moduleType
              ? moduleElements.find(
                  (e) =>
                    e.element_type === a.type &&
                    e.element_position === a.position
                )
              : null;

            // An element activity only belongs to modules of its own module
            // type that actually contain that element.
            if (
              a.moduleType &&
              (a.moduleType !== modules[i].module_type || !element)
            ) {
              return;
            }

            tmpTasks.push({
              title: a.activity,
              due: task.due,
              parent: task.id,
              sbscode: element ? element.element_id : null,
              project: this.project.id,
              license: this.project.license,
              options: {
                type: 'dhme-module-assembly-station',
                custom_1: module_id,
                custom_2: task.task_type.custom_3 ?? locationItem.location.id,
                custom_3: a.deviation
                  ? null
                  : element
                    ? element.element_id
                    : a.id,
                custom_4: a.position ?? null,
                custom_5: a.order ?? 0,
                custom_6: a.step,
                custom_7: a.leanforms_q_nr ?? null,
                custom_8: a.leanforms_remaining_point ?? null,
                custom_9: a.btn_options ?? null,
                metadata: a.translations ?? null,
              },
            });
          });

          tasks = [...tasks, ...tmpTasks];

          locationStatuses.push({
            id: null,
            module_id: module_id,
            assembly_location: locationItem.location.id,
            status: selectedActivities.some((t) => t.deviation)
              ? 'alert'
              : 'success',
          });
        }

        if (skippedModules.length) {
          this.$store.commit('showNotification', {
            content: `Skipped ${skippedModules.length} module(s) that already generated tasks on this location.`,
            color: 'info',
          });
        }

        if (modulesWithoutAssemblyTask.length) {
          this.$store.commit('showNotification', {
            content: `No assembly task found for ${
              modulesWithoutAssemblyTask.length
            } module(s): ${modulesWithoutAssemblyTask.slice(0, 5).join(', ')}${
              modulesWithoutAssemblyTask.length > 5 ? ', …' : ''
            }. These were skipped.`,
            color: 'warning',
          });
        }

        // Completing a location without any selected activities is valid: it
        // only writes the location statuses and skips the task import.
        const steps = [...new Set(tasks.map((x) => x.options.custom_6))];
        const totalTasks = tasks.length;
        let processed = 0;

        this.setProgress({
          stepLabel: steps.length > 0 ? String(steps[0]) : '-',
          processed,
          total: totalTasks,
        });

        for (const step of steps) {
          const stepTasks = tasks.filter((t) => t.options.custom_6 === step);
          const batches = this.chunkArray(
            stepTasks,
            this.importProgress.batchSize
          );

          this.setProgress({
            stepLabel: String(step),
            batchCurrent: batches.length > 0 ? 1 : 0,
            batchTotal: batches.length,
          });

          for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            this.setProgress({
              batchCurrent: batchIndex + 1,
              batchTotal: batches.length,
            });

            await this.$store.dispatch(
              'importAssemblyPreparationsTasks',
              batches[batchIndex]
            );

            processed += batches[batchIndex].length;
            this.setProgress({ processed, total: totalTasks });
          }

          this.$store.commit('showNotification', {
            content: `Imported all tasks for Step: '${step}'`,
            color: 'info',
          });
        }

        if (tasks.length) {
          await this.$store.dispatch(
            'fetchAssemblyPreparationsModuleAssemblyTasks',
            {
              projectId: this.project.id,
            }
          );
        }

        if (locationStatuses.length) {
          await this.$store.dispatch('setLocationStatuses', locationStatuses);
        }

        locationItem.pending = modulesWithoutAssemblyTask.length;
        locationItem.completed = modulesWithoutAssemblyTask.length === 0;

        // Start the next batch on this location clean, so a module type
        // change does not carry these ticks and deviations over.
        locationItem.activities = locationItem.activities.filter(
          (a) => !a.deviation
        );
        locationItem.activities.forEach((a) => {
          a.selected = false;
        });
        locationItem.toggle = false;
      } catch (e) {
        this.$store.commit('showNotification', {
          content: e?.message ?? 'Import failed',
          color: 'error',
        });
      } finally {
        locationItem.loading = false;
        this.importing = false;
        this.resetProgress();
      }
    },

    assemblyLocationSteps(location) {
      return this.assemblyConfigurationSteps
        .filter((s) =>
          Array.from(new Set(location.activities.map((a) => a.step))).includes(
            s.id
          )
        )
        .sort((a, b) => parseInt(a.order) - parseInt(b.order));
    },

    updateOrderToDeviationTask(activity) {
      activity.order = parseInt(activity.order);
      this.deviationOrderMenus[activity.id] = false;
    },

    addLocationDeviation(item, step) {
      const generateHash = (locationId, stepId) => {
        return `${locationId}-${stepId}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 8)}`;
      };

      const uniqueId = generateHash(item.location.id, step.id);

      item.activities.push({
        id: uniqueId,
        assembly_location: item.location.id,
        deviation: true,
        selected: true,
        activity: this.locationDeviations[item.location.id],
        order: 99,
        step: step.id,
      });
      this.locationDeviations[item.location.id] = null;
      this.locationDeviationMenus[`${item.location.id}-${step.id}`] = false;
    },

    closeDialog() {
      if (this.importing) {
        this.$store.commit('showNotification', {
          content: 'Import in progress. Please wait until it finishes.',
          color: 'info',
        });
        return;
      }

      clearTimeout(this.applyTimer);
      this.applyTimer = null;
      this.pendingModuleTypes = [];
      this.moduleTypes = [];
      this.elementsCache = {};
      this.previewType = null;
      this.previewToken++;
      this.loadingPreview = false;
      this.preservedSelection = null;
      this.elements = [];
      this.documentPreview = null;
      this.locationActivities = [];
      this.locationDeviations = {};
      this.$emit('closeDialog');
    },
  },
};
</script>

<style scoped>
.preview-type-activity {
  color: var(--v-primary-base);
}
</style>
